import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown, ChevronUp, Mail, MessageCircle, Search } from 'lucide-react';
import { Footer } from '../components/layout/Footer';

const SUPPORT_EMAIL = 'support@vowvisionsai.com';

const categories = [
  {
    id: 'getting-started',
    label: 'Getting Started',
    faqs: [
      {
        q: 'How do I create my first invitation?',
        a: 'Head to the Create page and fill in your event details — partner names, date, venue, dress code, and tone. Our AI will generate a polished invitation design in around 4 minutes. You can then refine, preview, and export it directly from the editor.'
      },
      {
        q: 'Do I need to create an account to use VowVisionsAI?',
        a: 'You can preview the Create flow without an account, but saving your invitations to the library and exporting files requires a free account. Sign up takes under 30 seconds with your email or Google.'
      },
      {
        q: 'What information do I need to get started?',
        a: 'At minimum: the names of both partners and your event date. Everything else — venue, dress code, tone, style — helps the AI generate a more personalised design, but nothing beyond names and date is required to start.'
      },
      {
        q: 'What styles and tones are available?',
        a: 'We currently offer Romantic Classic, Soft Modern, and Floral Boho styles, with more being added regularly. Each style can be tuned with tones like Romantic, Modern, Playful, or Formal to match the feel of your event.'
      },
    ]
  },
  {
    id: 'designs-exports',
    label: 'Designs & Exports',
    faqs: [
      {
        q: 'What export formats are available?',
        a: 'Depending on your plan: PNG (all plans), PDF print-ready A5 (Essential and above), and Instagram/story-format image (Signature and Heirloom). The Heirloom plan includes the full export suite.'
      },
      {
        q: 'Can I edit my invitation after it has been generated?',
        a: 'Yes. All generated invitations are saved to your library where you can open them in the editor to update text, switch styles, or regenerate variations. Changes are saved automatically.'
      },
      {
        q: 'How many invitation concepts can I create?',
        a: 'Essential: 1 invitation project. Signature: 3 invitation concepts. Heirloom: unlimited generations. During the launch promo period, these limits apply to the promotional plans.'
      },
      {
        q: 'Can I use my invitation designs commercially (for clients)?',
        a: 'Personal and direct event use is included in all plans. If you are a wedding planner or designer creating invitations for paying clients, please contact us at support@vowvisionsai.com to discuss a commercial licence.'
      },
      {
        q: 'Why does my export look different from the preview?',
        a: 'The in-app preview is a live web render. PDF and print exports are processed separately and may render fonts or spacing slightly differently. If you notice a significant difference, contact our support team with a screenshot and we will investigate.'
      },
    ]
  },
  {
    id: 'account-billing',
    label: 'Account & Billing',
    faqs: [
      {
        q: 'How do I cancel my subscription?',
        a: 'Go to Account → Subscription → Cancel plan. Cancellation takes effect at the end of your current billing period. You will retain full access until then and your saved invitations will remain accessible.'
      },
      {
        q: 'Can I change my plan at any time?',
        a: 'Yes. You can upgrade or downgrade at any time from your Account settings. Upgrades take effect immediately. Downgrades take effect at the start of the next billing period.'
      },
      {
        q: 'Do you offer refunds?',
        a: 'We do not automatically offer refunds for partial billing periods. If you experience a technical issue that prevented you from using the Service, please contact support@vowvisionsai.com within 7 days and we will review your case individually.'
      },
      {
        q: 'When will Stitch Express payment be available?',
        a: 'Stitch Express payment integration is coming soon. Until then, the Service remains available on the launch promotional pricing. You will receive advance notice before any payment is required.'
      },
      {
        q: 'How do I delete my account?',
        a: 'Go to Account → Settings → Delete account. This will permanently delete all your invitation data within 30 days. This action cannot be undone. If you need help, email support@vowvisionsai.com.'
      },
    ]
  },
  {
    id: 'privacy-security',
    label: 'Privacy & Security',
    faqs: [
      {
        q: 'Who can see my invitation designs?',
        a: 'Only you. Your invitation designs are private to your account and are never shared with other users or made publicly accessible unless you explicitly export and share the file yourself.'
      },
      {
        q: 'Is my data secure?',
        a: 'Yes. All data is encrypted in transit (TLS/HTTPS) and at rest. We use secure authentication via Supabase and do not store payment card data. See our Privacy Policy for full details.'
      },
      {
        q: 'Does VowVisionsAI use my invitation content to train AI models?',
        a: 'No. The content you enter (names, dates, venue details) is used only to generate your invitation designs. We do not use your personal event data to train AI models.'
      },
      {
        q: 'How do I request a copy of my data or ask for it to be deleted?',
        a: 'Email privacy@vowvisionsai.com with your request. We will respond within 30 days in compliance with GDPR, CCPA, and POPIA requirements. You can also delete your account directly from the Account settings page.'
      },
    ]
  },
  {
    id: 'technical',
    label: 'Technical Issues',
    faqs: [
      {
        q: 'The AI is taking longer than usual — what should I do?',
        a: 'AI generation usually takes under 4 minutes. During high-traffic periods it may take slightly longer. If generation is stuck for more than 10 minutes, try refreshing the page. Your inputs are auto-saved, so you will not lose your details.'
      },
      {
        q: 'My invitation is not saving — how do I fix this?',
        a: 'Make sure you are logged in (the Library tab requires an account). If you are logged in and still experiencing save issues, check your internet connection and try again. Persistent issues should be reported to our support team.'
      },
      {
        q: 'The website is not loading correctly on my device — what can I do?',
        a: 'VowVisionsAI works best on modern browsers (Chrome, Safari, Firefox, Edge). Try clearing your browser cache, disabling browser extensions, or trying a different browser. If the issue persists, please contact support with your device and browser details.'
      },
      {
        q: 'Can I use VowVisionsAI on my phone?',
        a: 'Yes. VowVisionsAI is fully responsive and designed to work on mobile and tablet devices. The Create flow, library, and export functions are all accessible on mobile. A dedicated iOS app is in development.'
      },
    ]
  }
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#F0E8DC] last:border-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-start justify-between gap-4 py-5 text-left"
      >
        <span className="text-sm font-medium leading-7 text-[#211D1A]">{faq.q}</span>
        {open
          ? <ChevronUp size={18} className="mt-0.5 shrink-0 text-[#C6A14B]" />
          : <ChevronDown size={18} className="mt-0.5 shrink-0 text-[#9B8E80]" />
        }
      </button>
      {open && (
        <p className="pb-5 text-sm leading-8 text-[#5E564F]">{faq.a}</p>
      )}
    </div>
  );
}

export default function HelpPage() {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '', sent: false, sending: false });

  const filtered = search.trim().length > 1
    ? categories.flatMap((c) => c.faqs.filter(
        (f) => f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase())
      )).map((faq) => ({ ...faq, _searchResult: true }))
    : null;

  const activeCategory_ = categories.find((c) => c.id === activeCategory);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormState((f) => ({ ...f, sending: true }));
    await new Promise((r) => setTimeout(r, 1200));
    setFormState((f) => ({ ...f, sending: false, sent: true }));
  }

  return (
    <div className="min-h-screen bg-[#FBF8F3] text-[#211D1A]">

      {/* Header */}
      <section className="border-b border-[#E8E0D6] bg-white pb-12 pt-32 md:pt-36">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.35em] text-[#8A7A6A]">Support</p>
          <h1 className="font-serif text-4xl leading-tight text-[#211D1A] md:text-5xl">Help Center</h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-8 text-[#6C6258]">
            Find answers to common questions, or send us a message. Our team responds within 24 hours.
          </p>

          {/* Search */}
          <div className="mx-auto mt-8 max-w-xl">
            <div className="flex items-center gap-3 rounded-[28px] border border-[#E4D9CB] bg-white px-5 py-3 shadow-soft focus-within:border-[#C6A14B]">
              <Search size={18} className="shrink-0 text-[#9B8E80]" />
              <input
                type="text"
                placeholder="Search for answers..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-transparent text-sm text-[#211D1A] placeholder-[#9B8E80] outline-none"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ body */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6">

          {filtered ? (
            /* Search results */
            <div>
              <p className="mb-6 text-sm text-[#8A7A6A]">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{search}"</p>
              {filtered.length === 0 ? (
                <div className="rounded-[28px] border border-[#E8DDD1] bg-white p-10 text-center">
                  <p className="font-serif text-xl text-[#211D1A]">No results found</p>
                  <p className="mt-3 text-sm text-[#6C6258]">Try a different search term, or send us a message below.</p>
                </div>
              ) : (
                <div className="rounded-[28px] border border-[#E8DDD1] bg-white px-8">
                  {filtered.map((faq) => <FAQItem key={faq.q} faq={faq} />)}
                </div>
              )}
            </div>
          ) : (
            /* Category tabs + FAQs */
            <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
              {/* Sidebar */}
              <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-1">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCategory(c.id)}
                    className={`rounded-[20px] px-4 py-2.5 text-left text-sm font-medium transition ${
                      activeCategory === c.id
                        ? 'bg-[#1E1A17] text-white'
                        : 'bg-white border border-[#E4D9CB] text-[#6C6258] hover:border-[#C6A14B] hover:text-[#211D1A]'
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              {/* FAQs */}
              <div className="rounded-[28px] border border-[#E8DDD1] bg-white px-8">
                <h2 className="border-b border-[#F0E8DC] py-6 font-serif text-2xl text-[#211D1A]">
                  {activeCategory_?.label}
                </h2>
                {activeCategory_?.faqs.map((faq) => <FAQItem key={faq.q} faq={faq} />)}
              </div>
            </div>
          )}

          {/* Contact form */}
          <div className="mt-16 rounded-[32px] border border-[#E8DDD1] bg-white p-8 lg:p-10">
            <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8A7A6A]">Still need help?</p>
                <h2 className="mt-3 font-serif text-3xl leading-tight text-[#211D1A]">Send us a message</h2>
                <p className="mt-4 text-sm leading-7 text-[#6C6258]">Our support team is available 24/7. We aim to respond to every message within 24 hours.</p>
                <div className="mt-8 space-y-4">
                  <div className="flex items-center gap-3 text-sm text-[#6C6258]">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FBF8F3]">
                      <Mail size={16} className="text-[#C6A14B]" />
                    </div>
                    <span><a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#C6A14B] underline underline-offset-2">{SUPPORT_EMAIL}</a></span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#6C6258]">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FBF8F3]">
                      <MessageCircle size={16} className="text-[#C6A14B]" />
                    </div>
                    <span>Response time: within 24 hours</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-[#6C6258]">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#FBF8F3]">
                      <Check size={16} className="text-[#C6A14B]" />
                    </div>
                    <span>Available 24/7, every day</span>
                  </div>
                </div>
              </div>

              <div>
                {formState.sent ? (
                  <div className="flex h-full flex-col items-center justify-center rounded-[24px] bg-[#FBF8F3] p-8 text-center">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#F3E8D3]">
                      <Check size={24} className="text-[#C6A14B]" />
                    </div>
                    <p className="mt-5 font-serif text-2xl text-[#211D1A]">Message sent</p>
                    <p className="mt-3 text-sm text-[#6C6258]">Thanks for reaching out. We'll get back to you within 24 hours at <strong>{formState.email}</strong>.</p>
                    <button
                      onClick={() => setFormState({ name: '', email: '', subject: '', message: '', sent: false, sending: false })}
                      className="mt-6 text-sm text-[#C6A14B] underline underline-offset-2"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-[#8A7A6A]">Your name</label>
                        <input
                          type="text"
                          required
                          placeholder="Jane Smith"
                          value={formState.name}
                          onChange={(e) => setFormState((f) => ({ ...f, name: e.target.value }))}
                          className="w-full rounded-[16px] border border-[#E4D9CB] bg-[#FBF8F3] px-4 py-3 text-sm text-[#211D1A] placeholder-[#9B8E80] outline-none transition focus:border-[#C6A14B]"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-xs font-medium text-[#8A7A6A]">Email address</label>
                        <input
                          type="email"
                          required
                          placeholder="jane@example.com"
                          value={formState.email}
                          onChange={(e) => setFormState((f) => ({ ...f, email: e.target.value }))}
                          className="w-full rounded-[16px] border border-[#E4D9CB] bg-[#FBF8F3] px-4 py-3 text-sm text-[#211D1A] placeholder-[#9B8E80] outline-none transition focus:border-[#C6A14B]"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-[#8A7A6A]">Subject</label>
                      <input
                        type="text"
                        required
                        placeholder="What can we help you with?"
                        value={formState.subject}
                        onChange={(e) => setFormState((f) => ({ ...f, subject: e.target.value }))}
                        className="w-full rounded-[16px] border border-[#E4D9CB] bg-[#FBF8F3] px-4 py-3 text-sm text-[#211D1A] placeholder-[#9B8E80] outline-none transition focus:border-[#C6A14B]"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-[#8A7A6A]">Message</label>
                      <textarea
                        required
                        rows={5}
                        placeholder="Describe your issue or question in detail..."
                        value={formState.message}
                        onChange={(e) => setFormState((f) => ({ ...f, message: e.target.value }))}
                        className="w-full rounded-[16px] border border-[#E4D9CB] bg-[#FBF8F3] px-4 py-3 text-sm text-[#211D1A] placeholder-[#9B8E80] outline-none transition focus:border-[#C6A14B] resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={formState.sending}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1E1A17] px-6 py-4 text-sm font-medium text-white transition hover:bg-[#0E0C0B] hover:scale-[1.01] disabled:opacity-60"
                    >
                      {formState.sending ? 'Sending...' : 'Send message'}
                    </button>
                    <p className="text-center text-xs text-[#9B8E80]">
                      By submitting this form you agree to our{' '}
                      <Link to="/privacy" className="text-[#C6A14B] underline underline-offset-2">Privacy Policy</Link>.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Legal links */}
          <div className="mt-10 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[#8A7A6A]">
            <Link to="/privacy" className="hover:text-[#211D1A] hover:underline underline-offset-2">Privacy Policy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-[#211D1A] hover:underline underline-offset-2">Terms of Service</Link>
            <span>·</span>
            <Link to="/cookies" className="hover:text-[#211D1A] hover:underline underline-offset-2">Cookie Policy</Link>
            <span>·</span>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-[#211D1A] hover:underline underline-offset-2">{SUPPORT_EMAIL}</a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
